// src/pages/container_rental/ContainerRentalTimelinePage.tsx
//
// 임대 타임라인 — 발행 목록에서 분리된 독자 화면.
//
// 발행 목록(ContainerRentalListPage)과 동일한 페이지 헤더 패턴을 쓴다.
//  - 헤더: 제목 22px 700 + 서브 13.5px + 우측 [발행 목록](outlined) · [신규 등록](contained)
//  - 필터: q · status 만 (정렬은 간트에서 의미가 없어 hideSort)
//  - 본문: RentalTimeline 이 페이징 없이 자체 조회한다.
// 모바일에서도 그대로 렌더한다 — 차트 컨테이너 자체가 overflow-x 스크롤이라
// 페이지 body 가 밀리지 않는다.

import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";

import { RentalListParams } from "../../contracts/container_rental/types.ts";
import { designTokens } from "../../theme.ts";
import RentalSearchBar from "./components/RentalSearchBar.tsx";
import RentalTimeline from "./components/timeline/RentalTimeline.tsx";

const T = designTokens;

const C = {
  canvas: "#F6F7F8",
  ink: T.ink,
  sub: "#6B7178",
};

/** 타임라인은 페이징·정렬이 없다. q/status 만 실제로 쓰인다. */
const DEFAULT_PARAMS: RentalListParams = {
  q: "",
  status: "",
  page: 0,
  size: 20,
  sort: "updated_at_desc",
};

export default function ContainerRentalTimelinePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const inDashboard = location.pathname.startsWith("/dashboard");
  const basePath = inDashboard ? "/dashboard/container-rental" : "/container-rental";

  const [params, setParams] = useState<RentalListParams>(DEFAULT_PARAMS);

  const goCreate = () => navigate(`${basePath}/create`);
  const goList = () => navigate(basePath);

  const today = new Date();
  const stamp = `${today.getFullYear()}.${String(today.getMonth() + 1).padStart(2, "0")}.${String(today.getDate()).padStart(2, "0")}`;

  return (
    <Box
      sx={{
        // 발행 목록과 동일한 edge-to-edge 서피스. 대시보드 안에서는 부모 패딩을
        // 음수 마진으로 상쇄한다.
        position: "relative",
        m: inDashboard ? { xs: -2, md: -3 } : 0,
        p: { xs: 2, md: 3 },
        minHeight: inDashboard ? "calc(100vh - 65px)" : "100vh",
        bgcolor: C.canvas,
        fontFamily: T.fonts.sans,
      }}
    >
      {/* 페이지 헤더 */}
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", md: "flex-end" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            component="h1"
            sx={{
              fontFamily: T.fonts.sans,
              fontWeight: 700,
              fontSize: 22,
              lineHeight: 1.3,
              letterSpacing: "-0.01em",
              color: C.ink,
            }}
          >
            임대 타임라인
          </Typography>
          <Typography
            sx={{
              mt: 0.75,
              fontFamily: T.fonts.sans,
              fontSize: 13.5,
              color: C.sub,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            계약별 회차 일정과 연체 현황을 한눈에 봅니다 · {stamp} 기준
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1, flexShrink: 0, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            onClick={goList}
            startIcon={<FormatListBulletedIcon sx={{ fontSize: 18 }} />}
          >
            발행 목록
          </Button>
          <Button
            variant="contained"
            onClick={goCreate}
            startIcon={<AddIcon sx={{ fontSize: 18 }} />}
          >
            신규 등록
          </Button>
        </Box>
      </Box>

      {/* 정렬은 간트에 의미가 없다 — q/status 만 노출 */}
      <RentalSearchBar value={params} onChange={setParams} hideSort />

      <RentalTimeline
        q={params.q ?? ""}
        status={params.status ?? ""}
        onFallbackToCards={goList}
        fallbackLabel="발행 목록으로 보기"
        onCreateNew={goCreate}
      />
    </Box>
  );
}
